'use client';

import React, { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Sparkles, Loader2, Users, Star, Shield } from 'lucide-react';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';


const EXAMPLE_QUERIES = [
  'need a plumber in SW1A for a boiler service',
  'looking for an electrician near me urgently',
  'kitchen fitter in Manchester for full remodel',
  'gas engineer in Birmingham, need cert this week',
];

const ROTATING_EXAMPLES = [
  'e.g. I need a plumber in Leeds for a dripping tap',
  'e.g. Garden fence repair in Manchester, budget £400',
  'e.g. Electrician in Bristol to add sockets in kitchen',
  'e.g. Painter & decorator in Edinburgh, 3-bed house',
  'e.g. Roofer in Sheffield — tiles fell off in the wind (again)',
  'e.g. Plumber in London, my shower only does Arctic or volcano',
  'e.g. Carpenter in Birmingham to build a wardrobe IKEA defeated me',
  'e.g. Boiler service in Liverpool before my wife finds out it\'s broken',
  'e.g. Landscaper in Brighton, garden looks like a jungle documentary',
  'e.g. Tiler in Cardiff for bathroom, budget £600',
  'e.g. Handyman in Nottingham — 47 small jobs I\'ve been ignoring',
  'e.g. Electrician in Glasgow, need extra sockets for my "home office" (gaming setup)',
  'e.g. Fence installer in Southampton, 20 metres, close-board',
  'e.g. Boiler replacement in Birmingham, combi, 3-bed house',
  'e.g. Decorator in Bristol, living room and hallway, neutral tones',
  'e.g. Plumber in Manchester — radiator has been "nearly fixed" for 6 months',
  'e.g. Kitchen fitter in Sheffield, full remodel, budget £8,000',
  'e.g. Carpet fitter in Leicester, 2 bedrooms and landing',
  'e.g. Roofer in Cardiff, flat roof extension leaking',
  'e.g. Handyman in London — I own a drill but am afraid of it',
  'e.g. Patio layer in Coventry, 30sqm, Indian sandstone',
  'e.g. Gas engineer in Wolverhampton, annual service and cert',
  'e.g. Plasterer in Bristol, ceiling skim after leak repair',
  'e.g. Electrician in Leeds, consumer unit upgrade',
  'e.g. Painter in Edinburgh — magnolia, obviously, it\'s a rental',
];

const TRUST_STATS = [
  { label: '500+ Local Pros', icon: Users, key: 'stat-pros' },
  { label: '4.8 Avg Rating', icon: Star, key: 'stat-rating' },
  { label: 'Verified Profiles', icon: Shield, key: 'stat-verified' },
];

type ParseState = 'idle' | 'parsing' | 'done';

export default function HeroSection() {
  const router = useRouter();
  const [query, setQuery] = useState('');
  const [parseState, setParseState] = useState<ParseState>('idle');
  const [exampleIndex, setExampleIndex] = useState(0);
  const [exampleVisible, setExampleVisible] = useState(true);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setExampleVisible(false);
      setTimeout(() => {
        setExampleIndex((prev) => (prev + 1) % ROTATING_EXAMPLES.length);
        setExampleVisible(true);
      }, 400);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  const handleSearch = async () => {
    if (!query.trim()) {
      toast.error('Please describe what you need done');
      textareaRef.current?.focus();
      return;
    }
    setParseState('parsing');
    await new Promise((r) => setTimeout(r, 1200));
    setParseState('done');
    router.push(`/post-job?q=${encodeURIComponent(query.trim())}`);
  };

  return (
    <section className="relative pt-20 pb-12 md:pt-24 md:pb-16 overflow-hidden">
      {/* Hero background - geometric tools image with dark plum overlay (matches lovelocals.co.uk) */}
      <div className="absolute inset-0">
        <div
          className="w-full h-full"
          style={{
            backgroundImage: `url("/assets/images/Untitled_design_7_-1777046271886.png")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        {/* Dark plum overlay - bg-love-plum/75 */}
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(37, 12, 49, 0.75)' }} />
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-8 pt-8">
          <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 leading-tight">
            Find Trusted{' '}
            <span className="text-love-mint">Local Tradespeople</span>
          </h1>
          <p className="text-love-mint/80 text-lg md:text-xl max-w-xl mx-auto">
            Connect with verified service providers in your area
          </p>
        </div>

        {/* AI Search Box - matches lovelocals.co.uk search widget */}
        <div className="max-w-2xl mx-auto w-full">
          <div
            className="relative rounded-2xl p-1.5"
            style={{
              background: 'rgba(255,255,255,0.10)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.20)',
              boxShadow: '0 0 40px rgba(153,230,200,0.15)',
            }}
          >
            <textarea
              ref={textareaRef}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                if (parseState === 'done') setParseState('idle');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSearch();
                }
              }}
              placeholder="Where are you and what do you need?"
              rows={3}
              className="w-full rounded-xl px-5 py-4 text-base md:text-lg text-gray-900 placeholder-gray-400 resize-none focus:outline-none focus:ring-2 min-h-[100px]"
              style={{
                background: 'rgba(255,255,255,0.95)',
                focusRingColor: 'rgba(153,230,200,0.5)',
              }}
              aria-label="Describe your job"
            />
            <div className="flex items-center justify-between px-2 pt-1.5 pb-1">
              <p
                className="text-xs md:text-sm text-white/60 italic transition-opacity duration-400"
                style={{ opacity: exampleVisible ? 1 : 0 }}
              >
                {ROTATING_EXAMPLES[exampleIndex]}
              </p>
              <button
                onClick={handleSearch}
                disabled={parseState === 'parsing'}
                className="flex items-center justify-center gap-2 px-8 py-3 rounded-xl text-base font-semibold shrink-0 transition-all duration-200 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: '#99E6C8',
                  color: '#250C31',
                  boxShadow: '0 0 20px rgba(153,230,200,0.3)',
                }}
              >
                {parseState === 'parsing' ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Matching...
                  </>
                ) : (
                  <>
                    <Sparkles size={18} />
                    Go
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Trust stats row - matches lovelocals.co.uk */}
        <div className="flex flex-wrap justify-center gap-8 mt-10 text-white/80">
          {TRUST_STATS.map(({ label, icon: Icon, key }) => (
            <div key={key} className="flex items-center gap-2">
              <Icon size={20} className="text-love-mint" />
              <span className="text-sm">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}